import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { orderBy } from 'lodash';
import { createSelector } from 'reselect';
import { EntityTypes } from '../../../common/PricePrgClient';
import {
    PalletTypeEditor,
    ProductCategoryEditor,
    ProductGroupEditor
} from '../../../common/datagrid/hooks/AgGridEditors';
import { RootState } from '../../../store';
import { includes, notNull, startsWith, toLowerCase } from '../../../utils';
import { getApplicationRestrictions } from '../../settings/redux/settingsSelectors';
import { getWarehouses } from '../../stock/redux/stockSelectors';
import { ProductRow } from '../productTypes';
import { PalletTypeFloatingFilterComponent } from '../../../common/datagrid/components/PalletTypeFloatingFilterComponent';

export const getProducts = (state: RootState) => state.product.products;

export const getProductsSorting = (state: RootState) => state.product.table.sorting;
export const getProductCategories = (state: RootState) => state.product.productCategories;
export const getIsProductsLoading = (state: RootState) => state.product.isLoading;
export const getProductCategorySearchString = (state: RootState) => state.product.categorySearchString;
export const getProductFilters = (state: RootState) => state.product.table.filters;
export const getProductGroups = (state: RootState) => state.product.productGroups;
export const getSelectedWarehouse = (state: RootState) => state.product.selectedWarehouse;
export const getProductsTable = (state: RootState) => state.product.table;

export const getFilteredProductCategories = createSelector(
    [getProductCategorySearchString, getProductCategories],
    (searchString, productCategories) =>
        Object.values(productCategories).filter(cat => includes(searchString, cat.name))
);

const getProductTableAllColumnKeys = (state: RootState) => state.product.table.columns;

export const getProductTableVisibleColumnKeys = createSelector([getProductTableAllColumnKeys], columns =>
    columns.filter(x => !x.hide).map(x => x.key)
);

export const getProductRows = createSelector(
    [
        getProducts,
        getProductsSorting,
        getProductFilters,
        getProductCategories,
        getProductGroups,
        getSelectedWarehouse,
        getWarehouses
    ],
    (products, sorting, filters, productCategories, productGroups, selectedWarehouseId, warehouses) => {
        const selectedWarehouse = selectedWarehouseId != null ? warehouses[selectedWarehouseId] : null;

        const productRows: ProductRow[] = Object.values(products)
            .map(
                product =>
                    [
                        product,
                        productCategories[product.productCategory1Id!],
                        productCategories[product.productCategory2Id!],
                        productGroups[product.productGroupId!]
                    ] as const
            )
            .filter(
                ([p, pc1, pc2, pg]) =>
                    (selectedWarehouse?.warehouseId == null ||
                        p.warehouseIds?.includes(selectedWarehouse?.warehouseId ?? 0)) &&
                    includes(filters.productName, p.name) &&
                    startsWith(filters.productId, p.productId.toString()) &&
                    includes(filters.productCategory1Id, pc1?.name) &&
                    includes(filters.productCategory2Id, pc2?.name) &&
                    includes(filters.productGroupId, pg?.name) &&
                    includes(filters.sortOrder, p.sortOrder) &&
                    startsWith(filters.netWeight, p.netWeight.toString()) &&
                    startsWith(filters.grossWeight, p.grossWeight.toString()) &&
                    startsWith(filters.conversionFactor, p.conversionFactor?.toString()) &&
                    startsWith(filters.countryOfOrigin, p.countryOfOrigin?.toString()) &&
                    startsWith(filters.priceUnit, p.priceUnit) &&
                    startsWith(filters.vat, p.vat?.toString()) &&
                    includes(filters.customAttribute1, p.customAttribute1)
            )
            .map(([p, , , pg]) => ({
                productName: p.name,
                productId: p.productId,
                productCategory1Id: p.productCategory1Id,
                productCategory2Id: p.productCategory2Id,
                productGroupId: p.productGroupId,
                productGroupName: pg?.name ?? '-',
                sortOrder: p.sortOrder ?? '-',
                netWeight: p.netWeight,
                grossWeight: p.grossWeight,
                conversionFactor: p.conversionFactor,
                countryOfOrigin: p.countryOfOrigin,
                priceUnit: p.priceUnit,
                vat: p.vat,
                customAttribute1: p.customAttribute1,
                hidePrices: p.hidePrices,
                boxesPerPallet: p.boxesPerPallet,
                palletType: p.palletType
            }));

        const column = sorting.column === 'productCategory' ? 'productCategoryName' : sorting.column;
        const nullOrderPlacement = sorting.direction === 'asc' ? Infinity : -Infinity;

        return orderBy(
            productRows,
            [o => toLowerCase(o[column]) || nullOrderPlacement],
            sorting.direction
        );
    }
);

export const getColumnDefs = createSelector(
    [getProductGroups, getProductTableVisibleColumnKeys, getApplicationRestrictions, getProductCategories],
    (productGroups, columnKeys, appRestrictions, productCategories) => {
        const commonColumnProps = {
            // filterRenderer: DefaultFilterRenderer,
            sortable: true,
            resizable: true,
            floatingFilter: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponentParams: {
                suppressFilterButton: true
            }
        };

        const columns: ColDef<ProductRow>[] = (
            [
                {
                    field: 'hidePrices',
                    headerName: 'Hide prices',
                    width: 92
                },
                {
                    field: 'productId',
                    headerName: 'Product id',
                    width: 100
                },
                {
                    field: 'productName',
                    headerName: 'Product name',
                    width: 300,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                },
                {
                    field: 'productCategory1Id',
                    headerName: 'Product category 1',
                    width: 160,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false,
                    cellRenderer: ({ value }: ICellRendererParams<ProductRow>) => {
                        if (value != null) {
                            return productCategories[value]?.name ?? null;
                        } else {
                            return null;
                        }
                    },
                    cellEditor: ProductCategoryEditor,
                    cellEditorPopup: false,
                    cellEditorParams: {
                        productCategories: Object.values(productCategories).filter(c => c.level == 1),
                        allowNull: true,
                        parseValue: value => {
                            return productCategories[value]?.name ?? null;
                        },
                        cellHeight: 20
                    }
                },
                {
                    field: 'productCategory2Id',
                    headerName: 'Product category 2',
                    width: 160,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false,
                    cellRenderer: ({ value }: ICellRendererParams<ProductRow>) => {
                        if (value != null) {
                            return productCategories[value]?.name ?? null;
                        } else {
                            return null;
                        }
                    },
                    cellEditor: ProductCategoryEditor,
                    cellEditorPopup: false,
                    cellEditorParams: {
                        productCategories: Object.values(productCategories).filter(c => c.level == 2),
                        allowNull: true,
                        parseValue: value => {
                            return productCategories[value].name;
                        },
                        cellHeight: 20
                    }
                },
                {
                    field: 'productGroupId',
                    headerName: 'Product group',
                    width: 180,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false,
                    cellRenderer: ({ value }: ICellRendererParams<ProductRow>) => {
                        if (value != null) {
                            return productGroups[value]?.name ?? null;
                        } else {
                            return null;
                        }
                    },
                    cellEditor: ProductGroupEditor,
                    cellEditorPopup: false,
                    cellEditorParams: {
                        productGroups: Object.values(productGroups),
                        allowNull: true,
                        parseValue: value => {
                            return productGroups[value].name;
                        },
                        cellHeight: 20
                    }
                },
                {
                    field: 'sortOrder',
                    headerName: 'Sort order',
                    width: 180,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                },
                {
                    field: 'netWeight',
                    headerName: 'Net weight',
                    width: 120,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                    // filterRenderer: DefaultFilterRenderer,
                    // valueFormatter: NumberFormatter
                },
                {
                    field: 'grossWeight',
                    headerName: 'Gross weight',
                    width: 120,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                    // valueFormatter: NumberFormatter
                },
                {
                    field: 'conversionFactor',
                    headerName: 'Conversion factor',
                    width: 120,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                    // valueFormatter: NumberFormatter
                },
                {
                    field: 'boxesPerPallet',
                    headerName: 'Boxes per Pallet',
                    width: 120,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                    // valueFormatter: NumberFormatter
                },
                {
                    field: 'palletType',
                    headerName: 'Pallet type',
                    width: 80,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false,
                    floatingFilterComponent: PalletTypeFloatingFilterComponent,
                    cellRenderer: ({ value }: ICellRendererParams<ProductRow>) => {
                        if (value != null) {
                            return value == 0 ? 'EUR' : 'SPA';
                        } else {
                            return null;
                        }
                    },
                    cellEditor: PalletTypeEditor,
                    cellEditorPopup: false,
                    cellEditorParams: {
                        parseValue: value => {
                            return productGroups[value].name;
                        },
                        cellHeight: 20
                    }
                },
                {
                    field: 'countryOfOrigin',
                    headerName: 'Country',
                    width: 80,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                },
                {
                    field: 'priceUnit',
                    headerName: 'Price unit',
                    width: 120,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                },
                {
                    field: 'vat',
                    headerName: 'Vat (%)',
                    width: 120,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                    //  valueFormatter: NumberFormatter
                },
                {
                    field: 'customAttribute1',
                    headerName: 'EMV',
                    width: 60,
                    editable: !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false
                }
            ] as ColDef<ProductRow>[]
        ).map(column => ({
            ...column,
            ...commonColumnProps
        }));

        return columnKeys
            .map(key => columns.find(col => col.field === key))
            .filter(notNull) as ColDef<ProductRow>[];
    }
);
